import { useAppDispatch } from '@/app'
import { actions as shellActions } from '@/shell'
import dayjs from 'dayjs'
import { useEffect, useRef } from 'react'

/** True when `currentDay` is a different (non-empty) local day than `prevDay`. */
export const isNewLocalDay = (prevDay: string, currentDay: string): boolean =>
  prevDay !== '' && currentDay !== '' && currentDay !== prevDay

const localToday = () => dayjs().format('YYYY-MM-DD')

/** How often to check whether the local day rolled over (covers foreground sessions). */
const DAY_CHECK_INTERVAL_MS = 30_000

/**
 * Keeps the calendar date synced to "today" WITHOUT depending on the window
 * 'focus' event.
 *
 * The calendar date is otherwise reset to today only on `shell/appLaunched`,
 * which fires on app mount and on window 'focus'. On devices where 'focus' is
 * not reliably delivered (notably low-RAM Android resuming from a frozen/
 * backgrounded state), a session that crosses midnight keeps polling the
 * PREVIOUS day — so visits created for "today" never appear until a manual
 * reload. This hook dispatches `shell/dayChanged` (date reset, no splash side
 * effect) on the more reliable `visibilitychange` event and on a periodic
 * local-day check, so the date rolls over on resume and across midnight.
 */
export const useDateRollover = () => {
  const dispatch = useAppDispatch()
  const lastDayRef = useRef(localToday())

  // Sync to today whenever the app returns to the foreground (reliable where
  // window 'focus' is not). Mirrors the existing focus-driven reset.
  useEffect(() => {
    if (typeof document === 'undefined') {
      return
    }
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        return
      }
      lastDayRef.current = localToday()
      dispatch(shellActions.dayChanged())
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () =>
      document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [dispatch])

  // Catch a foreground session crossing midnight.
  useEffect(() => {
    const id = setInterval(() => {
      const today = localToday()
      if (isNewLocalDay(lastDayRef.current, today)) {
        lastDayRef.current = today
        dispatch(shellActions.dayChanged())
      }
    }, DAY_CHECK_INTERVAL_MS)
    return () => clearInterval(id)
  }, [dispatch])
}
